import React, { useEffect, useState, useRef, useMemo } from "react";
import CustomModal from "./CustomModal";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as yup from "yup";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { shareAssessment } from "../store/assessmentsSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchUsers } from "../store/userSlice";
import { Button, DatePicker, Input, InputNumber, Tag, Upload } from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";

const ShareAssessmentForm = ({ isShareOpen, setIsShareOpen, assessment }) => {
  const { users, loading, error } = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const ref = useRef(null);
  const [options, setOptions] = useState([]);

  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [fileList, setFileList] = useState([]);
  const user = useSelector((state) => state.users.singleRecord?.data);
  const companyId = user?.company;
 
  const handleShareClose = () => {
    setIsShareOpen(false);
  };

  useEffect(() => {
    if (users?.data?.length > 0) {
      setOptions(
        users?.data?.map((user) => ({ id: user.id, email: user.email }))
      );
    } else {
      setOptions([]);
    }
  }, [users, users?.data?.length, loading, error]);

  const shareAssessmentValidationSchema = yup.object({
    emails: yup
      .array()
      .of(yup.string().email("Invalid email format"))
      .test(
        "at-least-one",
        "At least one of Users, Emails, or Excel is required",
        function (emailsValue) {
          const { user_id, file } = this.parent; // Access sibling values (user_id and file)

          // Check if at least one of emails, user_id, or file is provided
          const hasEmail = Array.isArray(emailsValue) && emailsValue.length > 0;
          const hasUserId = user_id && user_id.length > 0;
          const hasFile = file && file !== null;

          // Only trigger error if none of the three fields (emails, user_id, file) is provided
          if (!hasEmail && !hasUserId && !hasFile) {
            return this.createError({
              message: "At least one of Users, Emails, or Excel is required",
            });
          }

          return true; // No error if one of them is provided
        }
      ),

    user_id: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required("User ID is required"),
        })
      )
      .test(
        "at-least-one",
        "At least one of Users, Emails, or Excel is required",
        function (userIdValue) {
          const { emails, file } = this.parent; // Access sibling values (emails and file)
          return (
            (Array.isArray(userIdValue) && userIdValue.length > 0) ||
            (Array.isArray(emails) && emails.length > 0) || // Ensure emails array has values
            (file && file !== null) // Ensure file exists and is not null
          );
        }
      ),

    file: yup
      .mixed()
      .nullable()
      .test(
        "at-least-one",
        "At least one of Users, Emails, or Excel is required",
        function (fileValue) {
          const { emails, user_id } = this.parent; // Access sibling values (emails and user_id)
          return (
            (fileValue && fileValue !== null) || // Ensure file exists and is not null
            (Array.isArray(emails) && emails.length > 0) || // Ensure emails array has values
            (Array.isArray(user_id) && user_id.length > 0) // Ensure user_id array has values
          );
        }
      ),

    expiry_hours: yup.string().required("Please enter expiry hours"),

    assessment_time_minutes: yup
      .string()
      .required("Please enter assessment time"),
  });

  const shareAssessmentFormik = useFormik({
    initialValues: {
      emails: [],
      user_id: [],
      expiry_hours: "",
      assessment_time_minutes: "",
      file: null,
    },
    validationSchema: shareAssessmentValidationSchema,
    onSubmit: async (values) => {
      const emailString =
        values?.emails?.length > 0 && values?.emails.join(",");
      const userIds = values?.user_id.map((user) => user.id);
      console.log(values, "sharevalues");
      const sendEmailAt = values.send_email_at
        ? new Date(values.send_email_at)
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
        : "";

      const data = new FormData();
      if (values.file) data.append("file", values.file);
      if (values.expiry_hours)
        data.append("link_expiry_time_hours", values.expiry_hours);
      if (values.assessment_time_minutes)
        data.append("assessment_time_minutes", values.assessment_time_minutes);
      if (values.send_email_at && sendEmailAt)
        data.append("send_email_at", sendEmailAt);
      if (userIds?.length > 0) data.append("user_ids", userIds);
      if (emailString) data.append("email_ids", emailString);
      data.append("assessment_id", assessment?.id);

      try {
        await dispatch(shareAssessment(data));
        setIsShareOpen(false);
        toast.success("Assessment shared successfully");
      } catch (error) {
        console.log(error, "error");
      }
    },
  });

  useEffect(() => {
    const queryParams = {};
    queryParams.search = "";
    companyId && (queryParams.company_id = companyId); // Add companyId 
    dispatch(fetchUsers(queryParams)); // Dispatch the fetchUsers action to get data
  }, [dispatch, companyId]);

  const { values, errors, touched, handleSubmit, isSubmitting } =
    shareAssessmentFormik;

  const handleSearch = (query) => {
    shareAssessmentFormik.setFieldTouched("user_id", true);
    const queryParams = {};
    if (query.trim() !== "") {
      queryParams.search = query;
    } else {
      queryParams.search = ""; // or any default search query
    }
    companyId && (queryParams.company_id = companyId);
    dispatch(fetchUsers(queryParams)); // Dispatch the fetchUsers action to get data
  };

  const handleSearchChange = (selectedItems) => {
    shareAssessmentFormik.setFieldTouched("user_id", true);
    shareAssessmentFormik.setFieldValue("user_id", selectedItems);
  };

  console.log(shareAssessmentFormik, "shareAssessmentFormik");

  // Function to handle adding email
  const handleAddEmail = () => {
    if (
      emailInput &&
      !emails.includes(emailInput) &&
      !shareAssessmentFormik?.errors?.emails
    ) {
      setEmails([...emails, emailInput]);
      setEmailInput("");
      shareAssessmentFormik.setFieldValue("emails", [...emails, emailInput]); // Sync with Formik
    }
  };

  // Handle pressing Enter key to add the email
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      shareAssessmentFormik.validateField("emails");
      handleAddEmail();
    }
  };

  // Handle change in input field
  const handleInputChange = (e) => {
    const newEmail = e.target.value;
    setEmailInput(newEmail);
    shareAssessmentFormik.setFieldTouched("emails", true);

    shareAssessmentFormik.setFieldValue("emails", [newEmail]);

    // Trigger Formik's email validation
    shareAssessmentFormik.validateField("emails");

    // Mark 'emails' as touched manually after adding the email
  };

  // Handle removing email
  const handleRemoveEmail = (removedEmail) => {
    setEmails(emails.filter((email) => email !== removedEmail));
    shareAssessmentFormik.setFieldValue(
      "emails",
      emails.filter((email) => email !== removedEmail)
    ); // Sync with Formik
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    shareAssessmentFormik.setFieldTouched("file", true);

    // Only allow a single file in the list
    if (newFileList.length > 1) {
      newFileList = [newFileList[0]];
    }
    setFileList(newFileList);
    shareAssessmentFormik.setFieldValue(
      "file",
      newFileList[0]?.originFileObj ?? null
    ); // Set file in Formik
  };

  const handleDateChange = (values, dateString) => {
    shareAssessmentFormik.setFieldValue("send_email_at", dateString);
  };

  return (
    <CustomModal
      show={isShareOpen}
      onHide={handleShareClose}
      modalTitle="Share Assessment"
    >
      <div className="mb-4 async-autocomplete">
        <label htmlFor="User" className="form-label label-title">
          Users
        </label>
        <AsyncTypeahead
          id="async-example"
          delay={0}
          searchText="Fetching users..."
          isLoading={loading}
          labelKey="email"
          minLength={2}
          isInvalid={touched?.user_id && errors?.user_id}
          onSearch={handleSearch}
          useCache={false}
          multiple
          options={options}
          placeholder="Search users..."
          clearButton
          ref={ref}
          onChange={handleSearchChange}
        />
        {touched?.user_id && errors?.user_id && (
          <small id="user-error" class="form-text text-danger">
            {errors?.user_id}
          </small>
        )}
      </div>
      <p className="text-center">OR</p>
      <div>
        <label htmlFor="Upload Excel" className="form-label label-title">
          Emails
        </label>
        <Input
          name="emails"
          value={emailInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder="Add emails and press enter"
          suffix={<PlusOutlined onClick={handleAddEmail} />}
          size="large"
          status={touched?.emails && errors?.emails ? "error" : "success"}
        />
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {emails.map((email, index) => (
            <Tag
              key={index}
              closable
              size="large"
              onClose={() => handleRemoveEmail(email)}
              style={{ margin: 5 }}
            >
              {email}
            </Tag>
          ))}
        </div>
        {touched?.emails && errors?.emails && (
          <small id="user-error" class="form-text text-danger">
            {errors?.emails}
          </small>
        )}
      </div>

      <p className="text-center mt-4">OR</p>
      <div className="mb-4">
        <label htmlFor="Upload Excel" className="form-label label-title">
          Upload Excel
        </label>
        <div>
          <Upload
            onRemove={(file) =>
              setFileList(fileList.filter((f) => f.uid !== file.uid))
            }
            fileList={fileList}
            beforeUpload={(file) => false} // Prevent automatic upload
            onChange={handleFileChange} // Handle file change
            showUploadList={true} // Hide default upload list
            accept=".xls,.xlsx" // Allow only Excel files
          >
            <Button
              danger={touched?.file && errors?.file}
              icon={<UploadOutlined />}
            >
              Select Excel File
            </Button>
          </Upload>
          {touched?.file && errors?.file && (
            <small id="user-error" class="form-text text-danger">
              {errors?.file}
            </small>
          )}
        </div>
      </div>

      {/* Assessment Time */}
      <div className="mb-4">
        <label
          htmlFor="Assessment Time Minutes"
          className="form-label label-title"
        >
          Assessment Time<span className="text-danger">*</span>
        </label>
        <div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            max={100}
            name="assessment_time_minutes"
            value={values.assessment_time_minutes}
            onChange={(e) => {
              shareAssessmentFormik.setFieldTouched(
                "assessment_time_minutes",
                true
              );
              shareAssessmentFormik.setFieldValue("assessment_time_minutes", e);
            }}
            placeholder="Enter assessment time"
            size="large"
            type="number"
            controls={false}
            status={
              touched?.expiry_hours && Boolean(errors?.expiry_hours)
                ? "error"
                : "success"
            }
          />
          {touched?.assessment_time_minutes &&
            touched?.assessment_time_minutes &&
            errors?.assessment_time_minutes && (
              <small id="user-error" class="form-text text-danger">
                {errors?.assessment_time_minutes}
              </small>
            )}
        </div>
      </div>

      {/* Expiry Hours */}
      <div className="mb-4">
        <label htmlFor="Upload Excel" className="form-label label-title">
          Expiry Hours<span className="text-danger">*</span>
        </label>
        <div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            max={100}
            name="expiry_hours"
            value={values.expiry_hours}
            onChange={(e) => {
              shareAssessmentFormik.setFieldTouched("expiry_hours", true);
              shareAssessmentFormik.setFieldValue("expiry_hours", e);
            }}
            placeholder="Enter expiry hours"
            size="large"
            type="number"
            controls={false}
            status={
              touched?.expiry_hours && Boolean(errors?.expiry_hours)
                ? "error"
                : "success"
            }
          />
          {touched?.expiry_hours &&
            touched?.expiry_hours &&
            errors?.expiry_hours && (
              <small id="user-error" class="form-text text-danger">
                {errors?.expiry_hours}
              </small>
            )}
        </div>
      </div>

      {/* send email date & time */}
      <div className="mb-4">
        <label htmlFor="Upload Excel" className="form-label label-title">
          Email send At
        </label>
        <div>
          <DatePicker
            style={{ width: "100%" }}
            // name="send_email_at"
            showTime
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onChange={(values, dateString) => {
              handleDateChange(values, dateString);
            }}
            size="large"
            disabledDate={(current) =>
              current && current < moment().startOf("day")
            } // Disable past dates
            disabledTime={(selectedMoment) => {
              const now = moment();
              if (selectedMoment.isSame(now, "day")) {
                return {
                  disabledHours: () => [...Array(now.hour()).keys()], // Disable past hours for today
                  disabledMinutes: (selectedHour) =>
                    selectedHour === now.hour()
                      ? [...Array(now.minute()).keys()] // Disable past minutes only for the current hour
                      : [], // Allow all minutes for future hours
                  disabledSeconds: (selectedHour, selectedMinute) =>
                    selectedHour === now.hour() &&
                    selectedMinute === now.minute()
                      ? [...Array(now.second()).keys()] // Disable past seconds only for the current minute
                      : [], // Allow all seconds for future minutes
                };
              }
              return {};
            }}
          />
        </div>
      </div>
      <Modal.Footer>
        <Button
          size="large"
          color="danger"
          variant="outlined"
          onClick={handleShareClose}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="outlined"
          loading={isSubmitting}
          type="primary"
          onClick={handleSubmit}
        >
          Save
        </Button>
      </Modal.Footer>
    </CustomModal>
  );
};

export default ShareAssessmentForm;
